import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router';
import { closeOutline } from 'ionicons/icons';
import { Icon } from './Icon';

export interface NavDrawerItem {
  title: string;
  to: string;
  /** Icon data from 'ionicons/icons'. */
  icon: string;
  /** Visibility classes for items that only apply at some viewport widths. */
  className?: string;
}

interface NavDrawerProps {
  id: string;
  open: boolean;
  /** Must be stable (useCallback); the open/close effect depends on it. */
  onClose: () => void;
  items: NavDrawerItem[];
}

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Replaces ion-menu: a panel that slides in from the left over a dimmed page.
 * Navigation only; identity and sign-out live in AccountMenu in the header,
 * which is present on every page where this drawer is not.
 *
 * Rendered through a portal because the header is sticky with a z-index,
 * which makes it a stacking context. A drawer rendered inside it could never
 * sit above the page content, whatever z-index it was given.
 *
 * Stays mounted while closed so it can animate out; `inert` keeps the closed
 * panel out of the tab order and away from screen readers.
 */
export function NavDrawer({ id, open, onClose, items }: NavDrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const returnFocusTo = document.activeElement as HTMLElement | null;

    // Lock page scroll, and pad for the scrollbar that disappears so the page
    // behind doesn't jump sideways on laptops.
    const { overflow, paddingRight } = document.body.style;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;

    closeButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;

      // Keep Tab inside the drawer. offsetParent is null for display:none
      // elements, which skips items hidden at the current viewport width.
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((element) => element.offsetParent !== null);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
      // Back to the menu button, unless navigation has already removed it.
      if (returnFocusTo?.isConnected) returnFocusTo.focus();
    };
  }, [open, onClose]);

  return createPortal(
    <div className={`fixed inset-0 z-50 ${open ? '' : 'pointer-events-none'}`} inert={!open}>
      <div
        aria-hidden="true"
        onClick={onClose}
        className={[
          'absolute inset-0 bg-scrim transition-opacity duration-200',
          open ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
      />

      <div
        ref={panelRef}
        id={id}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        className={[
          'absolute inset-y-0 left-0 flex w-[min(20rem,85vw)] flex-col bg-raised shadow-xl',
          'transition-[transform,visibility] duration-200 ease-out',
          open ? 'visible translate-x-0' : 'invisible -translate-x-full',
        ].join(' ')}
        // Clear the notch and home indicator on phones; viewport-fit=cover is
        // set in index.html, so without this the top items sit under the notch.
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/*
          No logo: the header behind the drawer already shows it. This row
          matches the header's height and padding, so on phones and tablets the
          close button lands exactly where the hamburger was, and opening and
          closing are the same tap. (On screens wider than the header's
          max-width the header content is centred, so they no longer line up.)
        */}
        <div className="flex h-14 shrink-0 items-center gap-2 border-b border-line px-3 lg:h-[70px] lg:px-4">
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="rounded-md p-2 text-content hover:bg-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
          >
            <Icon icon={closeOutline} className="size-6" />
          </button>
          <h2 id={`${id}-title`} className="text-base font-semibold text-content">
            Menu
          </h2>
        </div>

        <nav aria-label="Main" className="flex-1 overflow-y-auto py-2">
          <ul>
            {items.map((item) => (
              <li key={item.to} className={item.className}>
                <NavLink
                  to={item.to}
                  // Also closes when the link is to the current page, where
                  // no route change happens to close it for us.
                  onClick={onClose}
                  className={({ isActive }) =>
                    [
                      'flex items-center gap-3 border-l-4 px-4 py-3 text-base font-medium',
                      'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus',
                      isActive
                        ? 'border-primary bg-primary/10 text-content'
                        : 'border-transparent text-content-secondary hover:bg-hover hover:text-content',
                    ].join(' ')
                  }
                >
                  <Icon icon={item.icon} className="size-5 text-muted" />
                  {item.title}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>,
    document.body,
  );
}

